import { humanizeEligibility, resolveRoleEscalationEligibility } from "@swivl-mono/helpers";
import { APIScope, FeatureFlagKey, MemberInterface, MemberInviteInterface, MemberPermission, RoleEscalationEligibility } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, OldPanel, Select, SelectItem } from "@swivl-mono/ui";
import { useMemo } from "react";

import { ShowIf } from "../../../../../Components/ShowIf/ShowIf";
import { isFeatureEnabled } from "../../../../../Helpers/FeatureFlags/isFeatureEnabled";
import { useHasPermission } from "../../../../../Helpers/Permissions/useHasPermission";
import { Member } from "../../../../../Models/Member/Member.Current";
import MembersModel from "../../../../../Models/Member/Members.Model";
import RolesModel from "../../../../../Models/Roles/Roles.Model";
import { MemberPermissionsScopePanel } from "./MemberPermissionsScopePanel";

// Sentinel for the "inherit the role default" option — the Select uses a
// non-empty value, mapped back to `null` (no per-assignment override) on save.
const ELIGIBILITY_DEFAULT = "__default__";

interface Props {
  member?: MemberInterface;
  invite?: MemberInviteInterface;
  memberPermission: MemberPermission;
}

export function MemberOrInvitePermissionsRole(props: Props) {
  const { roles, defaultRoles, parentRoles, rolesLoaded } = RolesModel.useState();
  const { memberPermission } = props;
  const canManageMembers = useHasPermission(APIScope.MEMBERS_SET);
  const v3EscalationsEnabled = isFeatureEnabled(FeatureFlagKey.Snowy);

  const memberOrInvite = props.member ? props.member! : props.invite!;

  const dropdownItems = useMemo((): SelectItem[] => {
    const defaultsArray = defaultRoles ? Object.values(defaultRoles) : [];
    const parentArray = parentRoles ? Object.values(parentRoles) : [];
    const ownArray = roles ? Object.values(roles) : [];

    const items: SelectItem[] = [];
    if (defaultsArray.length > 0) {
      items.push({
        type: "group",
        label: "Default Roles",
        options: defaultsArray.map((role) => ({ value: role.slug, label: role.name }))
      });
    }
    if (parentArray.length > 0) {
      items.push({
        type: "group",
        label: "Inherited Roles",
        options: parentArray.map((role) => ({ value: role.slug, label: role.name }))
      });
    }
    if (ownArray.length > 0) {
      items.push({
        type: "group",
        label: Member.isInChildOrg ? "Custom Roles" : "Roles",
        options: ownArray.map((role) => ({ value: role.slug, label: role.name }))
      });
    }
    return items;
  }, [rolesLoaded]);

  let selectedRole;
  if (rolesLoaded && memberPermission?.roleSlug) {
    selectedRole = defaultRoles?.[memberPermission.roleSlug] ?? roles?.[memberPermission.roleSlug] ?? parentRoles?.[memberPermission.roleSlug];
  }

  const rolesBySlug = { ...(defaultRoles ?? {}), ...(parentRoles ?? {}), ...(roles ?? {}) };
  const resolvedRoleDefault = selectedRole ? resolveRoleEscalationEligibility(selectedRole, rolesBySlug) : undefined;

  const updatePermissions = (updated: MemberPermission) => {
    if (props.member) {
      let member = { ...props.member };
      let permissionIndex = member.permissions.findIndex((permission) => permission.id === updated.id);
      member.permissions[permissionIndex] = updated;
      MembersModel.updateMember(member);
    } else if (props.invite) {
      let invite = { ...props.invite };

      let permissionIndex = invite.permissions.findIndex((permission) => permission.id === updated.id);
      invite.permissions[permissionIndex] = updated;

      MembersModel.updateInvite(invite);
    }
  };

  return (
    <OldPanel className="mb-5">
      <div className="mb-1 flex flex-row items-center justify-center">
        <h4 className="m-0 flex-1">Role</h4>
        <ShowIf scope={APIScope.MEMBERS_SET}>
          <Button
            size={ButtonSize.SMALL}
            color={ButtonColor.ORANGE}
            onClickAsync={async () => {
              if (window.confirm("Are you sure you want to remove this role?")) {
                if (props.member) {
                  let member = { ...props.member };
                  let permissionIndex = member.permissions.findIndex((permission) => permission.id === memberPermission.id);
                  member.permissions.splice(permissionIndex, 1);
                  await MembersModel.updateMember(member);
                } else if (props.invite) {
                  let invite = { ...props.invite };
                  let permissionIndex = invite.permissions.findIndex((permission) => permission.id === memberPermission.id);
                  invite.permissions.splice(permissionIndex, 1);
                  await MembersModel.updateInvite(invite);
                }
              }
            }}
          >
            REMOVE
          </Button>
        </ShowIf>
      </div>

      <Select
        options={dropdownItems}
        selectedValues={selectedRole?.slug ? [selectedRole.slug] : []}
        disabled={!canManageMembers}
        hideClear
        onChange={(vals) => {
          const perms = { ...memberPermission };
          perms.roleSlug = vals[0];
          updatePermissions(perms);
        }}
      />
      {selectedRole && (
        <div className="mb-4">
          <i>{selectedRole.description}</i>
        </div>
      )}

      {/* Phase II: per-assignment escalation-eligibility override. Empty = inherit role default. Snowy-gated. */}
      {v3EscalationsEnabled && (
        <div className="mb-4">
          <label className="mb-1 block text-sm font-semibold text-grey-700">Escalation eligibility (this assignment)</label>
          <Select
            disabled={!canManageMembers}
            hideClear
            selectedValues={[memberPermission.escalationEligibility ?? ELIGIBILITY_DEFAULT]}
            options={[
              {
                value: ELIGIBILITY_DEFAULT,
                label: resolvedRoleDefault ? `(Use role default: ${humanizeEligibility(resolvedRoleDefault)})` : "(Use role default)"
              },
              { value: "all", label: "All — receive escalations and tickets" },
              { value: "tickets-only", label: "Tickets only — no live escalations" },
              { value: "none", label: "None — excluded" }
            ]}
            onChange={(vals) => {
              const value = vals[0];
              const perms = { ...memberPermission };
              perms.escalationEligibility = !value || value === ELIGIBILITY_DEFAULT ? null : (value as RoleEscalationEligibility);
              updatePermissions(perms);
            }}
          />
        </div>
      )}

      <h4>Locations</h4>
      <MemberPermissionsScopePanel
        memberOrInvite={memberOrInvite}
        memberPermission={memberPermission}
        updatePermissions={updatePermissions}
        readOnly={!canManageMembers}
      />
    </OldPanel>
  );
}
